/**
 * A request the portal signed for this browser alone (cmd/portal signedTune):
 * the body inside a signed RPC payload, or null unless it carries a valid
 * HMAC under this browser's tune_key and was made within the last minute.
 *
 * The key came with the join token, so nobody else in the room can make one:
 * LiveKit delivers the server's own RPC under a random caller identity, so who
 * is calling says nothing (TestTuneRPCCallerAgainstARealSFU). Used by the
 * operator's tune (MWE-9) and by a comrade's granted control (MWE-27); a body
 * says which it is with `kind`, so one cannot be replayed as the other.
 */
export async function verifiedSigned<T extends { at?: number; kind?: string }>(
  key: string,
  payload: string,
): Promise<T | null> {
  try {
    const { body, sig } = JSON.parse(payload) as { body?: string; sig?: string };
    if (typeof body !== 'string' || typeof sig !== 'string' || !/^[0-9a-f]{64}$/.test(sig)) return null;
    const enc = new TextEncoder();
    const k = await crypto.subtle.importKey('raw', enc.encode(key), { name: 'HMAC', hash: 'SHA-256' }, false, [
      'verify',
    ]);
    const mac = new Uint8Array((sig.match(/../g) ?? []).map((h) => parseInt(h, 16)));
    if (!(await crypto.subtle.verify('HMAC', k, mac, enc.encode(body)))) return null;
    const req = JSON.parse(body) as T;
    if (typeof req.at !== 'number' || Math.abs(Date.now() - req.at) > 60_000) return null;
    return req;
  } catch {
    return null;
  }
}
